import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { SurfaceConstants } from 'owa-calendar-surface-core';
import type ContextMenuInfo from '../../store/schema/ContextMenuInfo';
import TimeStripHeaderContextMenu from './TimeStripHeaderContextMenu';
import TimeStripHeader from './TimeStripHeader';
import type { IPoint } from '@fluentui/react/lib/Utilities';
import getTimeZones from '../../utils/getTimeZones';
import type { TimeStripConfig } from '../../interfaces/TimeStripConfig';

export interface TimeStripHeaderContentProps {
    timeZones?: string[];
    contextMenus?: ContextMenuInfo[];
    showContextMenu?: (timeZone: string) => boolean;
    timeStripConfig?: TimeStripConfig;
}

export default observer(function TimeStripHeaderContent(props: TimeStripHeaderContentProps) {
    const [contextMenuTimeZone, setContextMenuTimeZone] = React.useState<string | null>(null);
    const [contextMenuAnchorPoint, setContextMenuAnchorPoint] = React.useState<IPoint | null>(null);

    const onDismiss = React.useCallback(() => {
        {
            setContextMenuTimeZone(null);
            setContextMenuAnchorPoint(null);
        }
    }, []);
    const onContextMenu = React.useCallback((timeZoneId: string, anchorPoint: IPoint) => {
        {
            setContextMenuTimeZone(timeZoneId);
            setContextMenuAnchorPoint(anchorPoint);
        }
    }, []);

    const shouldShowContextMenu = (timeZone: string): boolean => {
        return props.showContextMenu ? props.showContextMenu(timeZone) : true;
    };

    const { contextMenus } = props;
    const timeZones = getTimeZones(props.timeZones, filterTimeZonesForHeader);
    const headerWidth =
        props.timeStripConfig?.headerWidth || SurfaceConstants.timeStripDefaultWidth;
    return (
        <>
            {timeZones
                .map((timeZoneId, idx) => (
                    <TimeStripHeader
                        key={timeZoneId ?? `timeStripHeader_${idx}`} // timeZoneId may be undefined so define a fallback key
                        timeZone={timeZoneId}
                        headerWidth={headerWidth}
                        onContextMenu={
                            contextMenus && shouldShowContextMenu(timeZoneId)
                                ? onContextMenu
                                : undefined
                        }
                    />
                ))
                .reverse()}
            {contextMenuTimeZone && (
                <TimeStripHeaderContextMenu
                    timeZone={contextMenuTimeZone}
                    anchorPoint={contextMenuAnchorPoint}
                    contextMenus={contextMenus}
                    onDismiss={onDismiss}
                />
            )}
        </>
    );
});

function filterTimeZonesForHeader(timeZones: string[]) {
    // Don't show the time zone label if there is only one time zone
    return timeZones?.length > 1 ? timeZones : [undefined];
}
